import React, { useRef, useEffect, useState } from 'react';
import styled from 'styled-components';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';

function useCountUp(target, durationSec, start) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!start) return;
    let rafId;
    let startTs = null;
    const tick = (ts) => {
      if (!startTs) startTs = ts;
      const progress = Math.min((ts - startTs) / (durationSec * 1000), 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setVal(Math.round(target * eased));
      if (progress < 1) rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [start, target, durationSec]);
  return val;
}

const Section = styled.section`
  background: #111111;
  border-top: 1px solid #1E1E1E;
  border-bottom: 1px solid #1E1E1E;
  padding: 72px 2rem;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) { padding: 56px 1.25rem; }
`;

const SideImg = styled(motion.img)`
  position: absolute;
  width: 200px;
  border-radius: 14px;
  object-fit: cover;
  box-shadow: 0 12px 40px rgba(0,0,0,0.5);
  pointer-events: none;

  @media (max-width: 1100px) { width: 150px; }
  @media (max-width: 768px)  { width: 100px; }
`;

const Inner = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const Item = styled(motion.div)`
  text-align: center;
  padding: 2rem 1.5rem;
  position: relative;

  &:not(:last-child)::after {
    content: '';
    position: absolute;
    right: 0;
    top: 15%;
    height: 70%;
    width: 1px;
    background: linear-gradient(to bottom, transparent, #2A2A2A, transparent);
  }

  @media (max-width: 640px) {
    &:not(:last-child)::after { display: none; }
    border-bottom: 1px solid #1E1E1E;
    &:last-child { border-bottom: none; }
  }
`;

const BigNum = styled.div`
  font-family: 'Barlow Condensed', sans-serif;
  font-size: clamp(3.5rem, 5.5vw, 5rem);
  font-weight: 900;
  color: #F47920;
  line-height: 1;
  letter-spacing: -0.02em;
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 0.05em;
`;

const Pre = styled.span`
  font-size: 0.6em;
  font-weight: 800;
`;

const Suf = styled.span`
  font-size: 0.52em;
  font-weight: 700;
`;

const BigText = styled.div`
  font-family: 'Barlow Condensed', sans-serif;
  font-size: clamp(1.5rem, 2.8vw, 2rem);
  font-weight: 800;
  color: #F47920;
  line-height: 1.1;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  padding: 0.6rem 0;
`;

const IconWrap = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 0.8rem;
  color: rgba(244,121,32,0.5);
  svg { width: 28px; height: 28px; }
`;

const Label = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: #fff;
  margin-top: 0.5rem;
  letter-spacing: 0.02em;
`;

const Desc = styled.div`
  font-size: 0.82rem;
  color: #555;
  margin-top: 0.3rem;
  line-height: 1.5;
  max-width: 210px;
  margin-left: auto;
  margin-right: auto;
`;

export default function Stats() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  const years = useCountUp(20, 1.8, inView);

  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const yLeft  = useTransform(scrollYProgress, [0, 1], [90, -90]);
  const yRight = useTransform(scrollYProgress, [0, 1], [-90, 90]);

  return (
    <Section ref={ref}>
      <SideImg
        src="/img/FOTOS FEREIMA/webp/image (20).webp"
        alt=""
        style={{ left: '-40px', bottom: '-20px', rotate: '-10deg', y: yLeft }}
      />
      <SideImg
        src="/img/FOTOS FEREIMA/webp/image (21).webp"
        alt=""
        style={{ right: '-40px', top: '-20px', rotate: '10deg', y: yRight }}
      />
      <Inner>
        {/* Stat 1 — Trayectoria */}
        <Item
          initial={{ opacity: 0, y: 24 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.55, delay: 0 }}
        >
          <BigNum>
            <Pre>+</Pre>{years}<Suf> Años</Suf>
          </BigNum>
          <Label>Trayectoria</Label>
          <Desc>Más de dos décadas de experiencia y dedicación en la industria cárnica.</Desc>
        </Item>

        {/* Stat 2 — Origen */}
        <Item
          initial={{ opacity: 0, y: 24 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.55, delay: 0.14 }}
        >
          <IconWrap>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <path d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
              <path d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
            </svg>
          </IconWrap>
          <BigText>San Juan<br />de los Lagos</BigText>
          <Label>Origen y Orgullo</Label>
          <Desc>Nacidos y consolidados en Jalisco. Nuestra tierra es nuestra identidad.</Desc>
        </Item>

        {/* Stat 3 — Alcance */}
        <Item
          initial={{ opacity: 0, y: 24 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.55, delay: 0.28 }}
        >
          <IconWrap>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <circle cx="12" cy="12" r="10"/>
              <line x1="2" y1="12" x2="22" y2="12"/>
              <path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/>
            </svg>
          </IconWrap>
          <BigText>Local<br />y Nacional</BigText>
          <Label>Alcance Estratégico</Label>
          <Desc>Cobertura estratégica con presencia en todo México.</Desc>
        </Item>
      </Inner>
    </Section>
  );
}
